/**
 * Brand — the AlphaConcreto logo lockup.
 *
 * Tries the photographic PNG first (`/brand/{name}.png`, supplied by the
 * design team) and falls back automatically to a hand-crafted SVG with
 * the brand's navy + gold palette when the PNG isn't there yet.
 *
 * Why two assets per variant:
 *   - The PNG is the canonical brand mark — what we ship in marketing,
 *     the App Store, etc. Dropped in `apps/web/public/brand/`.
 *   - The SVG (same folder, same basename) is a safety net so the page
 *     never shows a broken-image icon in dev / fresh checkouts.
 *
 * Three rendering variants:
 *   - `variant="horizontal"` (default): shield + "AlphaConcreto" wordmark.
 *   - `variant="mark"`: only the shield (square).
 *   - `variant="wordmark"`: only the textual wordmark.
 *     (Currently uses the same horizontal asset, cropped via CSS — design
 *     can ship a dedicated wordmark PNG later if needed.)
 */

import { useState } from 'react';
import { cn } from '@/lib/cn';

interface BrandProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'horizontal' | 'mark' | 'wordmark';
  /** Legacy prop. When false, behaves like variant='mark'. */
  withWordmark?: boolean;
  className?: string;
}

// md (the default) gets the horizontal lockup at 50 px so the wordmark
// reads comfortably in headers without forcing every call-site to
// specify a size. sm/lg/xl scaled around it.
const HEIGHT_MAP = {
  sm: 28,
  md: 50,
  lg: 64,
  xl: 80,
} as const;

/**
 * Asset basenames per variant. Resolution order:
 *   1. /brand/<name>.png  (real brand asset, photographic)
 *   2. /brand/<name>.svg  (committed fallback in this repo)
 *
 * Note: `wordmark` reuses the horizontal asset for now. When a
 * dedicated wordmark-only PNG ships, change the basename here.
 */
const ASSET_BASENAME: Record<NonNullable<BrandProps['variant']>, string> = {
  horizontal: 'horizontal',
  mark: 'shield',
  wordmark: 'horizontal',
};

export function Brand({
  size = 'md',
  variant,
  withWordmark = true,
  className,
}: BrandProps) {
  const v: NonNullable<BrandProps['variant']> =
    variant ?? (withWordmark ? 'horizontal' : 'mark');
  const h = HEIGHT_MAP[size];
  const basename = ASSET_BASENAME[v];

  // Start with the PNG; flip to SVG on the first error event. Tracking
  // by basename keeps the state reset when the parent swaps variants.
  const [fallback, setFallback] = useState(false);
  // `?v=` cache-buster: cuando se re-emite el PNG con corrección de marca
  // (ej. typo "Concrete" → "Concreto" en 2026-05) hay que invalidar el cache
  // de Cloudflare + navegadores, que sirven /brand/* con max-age=604800.
  // Subir este número cada vez que se publique un PNG nuevo.
  const src = `/brand/${basename}.${fallback ? 'svg' : 'png'}?v=2`;

  return (
    <img
      key={basename}
      src={src}
      alt="AlphaConcreto"
      className={cn('select-none', className)}
      style={{ height: h, width: 'auto' }}
      draggable={false}
      onError={() => {
        // First load failed — try the SVG sibling. If that also fails the
        // browser will show its default broken-image glyph, but at that
        // point we've genuinely lost both assets and a runtime fallback
        // wouldn't help.
        if (!fallback) setFallback(true);
      }}
    />
  );
}
